import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsContent } from '@/components/molecules/Tabs';
import { SegmentedTabsList } from '@/components/molecules/SegmentedTabs';
import { RankingItem } from '@/components/molecules/RankingItem';
import { Separator } from '@/components/atoms/Separator';
import { Spinner } from '@/components/atoms/Spinner';
import { ErrorState } from '@/components/molecules/ErrorState';
import { getCompanyRankings, getGlobalRankings } from '@/services/api/gamification.api';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { entranceAwareBlockTransition } from '@/constants/storyResourcesEntrance';
import styles from './RankingPage.module.scss';

const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
const HIDDEN = { opacity: 0, y: 20 };
const VISIBLE = { opacity: 1, y: 0 };

type RankingItemType = { rank: number; name: string; xp: number; isCurrentUser?: boolean };

export function RankingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());

  // Empresas internas (p. ej. "Tester"): sus usuarios solo ven el ranking global,
  // sin la pestaña de empresa. Se decide por dato (Company.hideCompanyRanking).
  const hideCompanyRanking = user?.company?.hideCompanyRanking === true;

  const fadeUp = (delay: number) => ({
    initial: HIDDEN,
    animate: pageReady ? VISIBLE : HIDDEN,
    transition: entranceAwareBlockTransition(pageReady, reducedMotion, 0.42, delay),
  });

  const [activeTab, setActiveTab] = useState('company');
  const [companyRanking, setCompanyRanking] = useState<RankingItemType[]>([]);
  const [globalRanking, setGlobalRanking] = useState<RankingItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadRankings = useCallback(
    async (showLoading = true) => {
      if (!user?.id) return;
      if (showLoading) setLoading(true);
      try {
        const [company, global] = await Promise.all([
          // La empresa Tester (u otra interna) no carga ni muestra ranking de empresa.
          user.companyId && !hideCompanyRanking
            ? getCompanyRankings(user.companyId) // todos los de la empresa
            : Promise.resolve([]),
          getGlobalRankings(), // sin límite → todos los alumnos
        ]);
        setCompanyRanking(
          company.map((r) => ({
            rank: r.rank,
            name: r.name,
            xp: r.totalXP,
            isCurrentUser: r.userId === user.id,
          }))
        );
        setGlobalRanking(
          global.map((r) => ({
            rank: r.rank,
            name: r.name,
            xp: r.totalXP,
            isCurrentUser: r.userId === user.id,
          }))
        );
        setLoadError(false);
      } catch {
        if (showLoading) {
          setCompanyRanking([]);
          setGlobalRanking([]);
          setLoadError(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [user?.id, user?.companyId, hideCompanyRanking]
  );

  useEffect(() => {
    void loadRankings();
  }, [loadRankings]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => { void loadRankings(false); }, 15000);
    return () => clearInterval(interval);
  }, [user?.id, loadRankings]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) void loadRankings(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user?.id, loadRankings]);

  // Render compartido de una lista de ranking (estados de carga/error/vacío + ítems).
  const renderList = (items: RankingItemType[], emptyLabel: string) => (
    <div className={styles.ranking__list}>
      {loading ? (
        <div className={styles.ranking__loading}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      ) : loadError ? (
        <ErrorState onRetry={() => void loadRankings(true)} />
      ) : items.length === 0 ? (
        <p>{emptyLabel}</p>
      ) : (
        items.map((item, index) => (
          <motion.div
            key={`${item.rank}-${item.name}`}
            initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
            whileInView={pageReady && !reducedMotion ? { opacity: 1, y: 0 } : undefined}
            viewport={{ once: true, margin: '-24px' }}
            transition={{ duration: 0.38, delay: Math.min(index * 0.06, 0.4), ease: EASE }}
          >
            <RankingItem
              rank={item.rank}
              name={item.name}
              xp={item.xp}
              isCurrentUser={item.isCurrentUser}
            />
            {index < items.length - 1 && <Separator />}
          </motion.div>
        ))
      )}
    </div>
  );

  return (
    <div className={styles.ranking}>
      <motion.div className={styles.ranking__header} {...fadeUp(0)}>
        <div>
          <h1 className={styles.ranking__title}>{t('rankingPage.title')}</h1>
          <p className={styles.ranking__subtitle}>{t('rankingPage.subtitle')}</p>
        </div>
      </motion.div>

      <motion.div {...fadeUp(0.18)}>
        {hideCompanyRanking ? (
          // Solo ranking global (sin pestañas).
          renderList(globalRanking, t('rankingPage.emptyGlobal'))
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <SegmentedTabsList
              value={activeTab}
              items={[
                { value: 'company', label: t('rankingPage.tabCompany') },
                { value: 'global', label: t('rankingPage.tabGlobal') },
              ]}
              ariaLabel={t('rankingPage.tabsAria')}
            />

            <TabsContent value="company">
              {renderList(companyRanking, t('rankingPage.emptyCompany'))}
            </TabsContent>

            <TabsContent value="global">
              {renderList(globalRanking, t('rankingPage.emptyGlobal'))}
            </TabsContent>
          </Tabs>
        )}
      </motion.div>
    </div>
  );
}

export default RankingPage;
